import { useEffect, useState } from "react";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";

import settingService from "api/settingsService";
import Input from "components/Input";
import HollowButton from "components/HollowButton";
import Button from "components/Button";
import { getErrorMessage } from "utils/getErrorMessage";
import { IDropdownObject } from "components/Dropdown/typings";
import InfoIcon from "assets/icons/info.svg?react";
import CloseIcon from "assets/icons/close.svg?react";
import {
  CourtPortalSyncState,
  ICourtPortalSettings,
  IUserCourtPortal,
} from "dto/Settings/ICourtPortalSettings";
import {
  ICourtPortalLoginResponse,
  ICourtPortalUser,
} from "dto/Settings/ICourtPortalLoginResponse";
import classNames from "classnames";
import Chip from "components/Chip";
import DropdownMui from "components/DropdownMui";
import moment, { Moment } from "moment";
import {
  Modal,
  Tooltip,
} from "@mui/material";
import VideoUrl from "../../../../public/video/courtportal.mp4?url";
import Spinner from "components/Spinner";
import { useTranslation } from "react-i18next";
import { ENotificationType, notification } from "utils/notification";

export enum CourtPortalSettingsStep {
  Login,
  Confirm,
  Settings,
  CaseSettings,
}

interface ICourtPortalSettingsTab {
  step: CourtPortalSettingsStep;
  name: string;
}

const CourtPortalSettings: FC<Props> = ({
  onCanceled,
  onSaved,
  handleDisconnect,
  refetchI,
  refetchportals,
  grow,
  createOnly,
}) => {
  const { t } = useTranslation();

  const TABS: ICourtPortalSettingsTab[] = [
    {
      step: CourtPortalSettingsStep.Settings,
      name: t("settings.courtPortal.loginAndAppeal"),
    },
    {
      step: CourtPortalSettingsStep.CaseSettings,
      name: t("settings.courtPortal.casesSync"),
    },
  ];

  const [step, setStep] = useState<CourtPortalSettingsStep | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: settingsData, isLoading: isLoadingSettings } =
    settingService.useGetCourtPortalSettings();
  const { mutate: loginCourtPortal, isLoading: isLoginLoading } =
    settingService.useLoginCourtPortal();
  const { mutate: confirmCourtPortal } = settingService.useConfirmCourtPortal();
  const { mutate: updateSettings } =
    settingService.useUpdateCourtPortalSettings();
  const { mutate: updateCaseSettings } = settingService.useSetCaseSettings();

  const [showTabs, setShowTabs] = useState<boolean>(false);
  const [syncActiveCases, setSyncActiveCases] = useState<boolean>(false);
  const [syncClosedCases, setSyncClosedCases] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<ICourtPortalSettingsTab>(TABS[0]);
  const { data: caseSettings } = settingService.useGetCourtPortalCaseSettings(
    !showTabs,
  );
  const [lastSync, setLastSync] = useState<Moment>(null);

  const [loginData, setLoginData] = useState({ login: "", pass: "" });
  const [courtPortalUser, setCourtPortalUser] =
    useState<ICourtPortalUser | null>(null);

  const [selectedPortals, setSelectedPortals] = useState<IUserCourtPortal[]>(
    [],
  );

  const [portals, setPortals] = useState<Array<IDropdownObject>>([]);

  const [prevButtonState] = useState({
    show: true && !grow,
    label: t("common.cancel"),
    disabled: false,
  });
  const [nextButtonState, setNextButtonState] = useState({
    show: true,
    label: t("common.finish"),
    disabled: false,
  });

  const [showVideo, setShowVideo] = useState<boolean>(false);

  const save = () => {
    switch (step) {
      case CourtPortalSettingsStep.Login:
        if (!selectedPortals.length) return;

        loginCourtPortal(
          {
            Login: loginData.login,
            Password: loginData.pass,
            PortalIds: selectedPortals.map((x) => x.Id),
          },
          {
            onSuccess(data) {
              const response: ICourtPortalLoginResponse = data.data;
              setError(null);
              setCourtPortalUser(response.User);
              setStep(CourtPortalSettingsStep.Confirm);
              // refetchportals();
            },
            onError(e) {
              setError(e.response.data.errorCodes[0].message);
            },
          },
        );
        break;
      case CourtPortalSettingsStep.Confirm:
        confirmCourtPortal(
          {
            Login: loginData.login,
          },
          {
            onSuccess() {
              setError(null);
              //onCanceled();
              refetchI();
              refetchportals();

              setStep(CourtPortalSettingsStep.Settings);
              setShowTabs(true);
              setActiveTab(TABS[1]);
            },
            onError(e: any) {
              setError(getErrorMessage(e.response.data));
            },
          },
        );
        break;
      case CourtPortalSettingsStep.Settings:
        updateSettings(
          {
            PortalIds: selectedPortals.map((x) => x.Id),
            DocumentIds: [],
          },
          {
            onSuccess() {
              setError(null);
              onSaved();
              refetchportals();
            },
            onError(e) {
              setError(e.response.data.errorCodes[0].message);
            },
          },
        );
      case CourtPortalSettingsStep.CaseSettings:
        updateCaseSettings(
          {
            SyncActive: syncActiveCases,
            SyncClosed: syncClosedCases,
          },
          {
            onSuccess() {
              setError(null);
              onSaved();
              refetchportals();
            },
            onError(e) {
              setError(e.response.data.errorCodes[0].message);
            },
          },
        );
        break;
    }
  };

  const onPortalChange = (element?: IDropdownObject) => {
    if (!element) {
      return;
    }

    const c = selectedPortals.find((x) => x.Id == element?.value);
    if (!c && element) {
      setSelectedPortals([
        ...selectedPortals,
        {
          Id: element.value as number,
          Name: element.name,
          IsActive: true,
          IsMain: false,
          RulesUrl: "",
          Url: "",
          Status: CourtPortalSyncState.NotRunning,
          LastSync: "",
          LastErrorMessage: "",
          isNew: true,
        },
      ]);
    }
  };

  const handleRemoveAdditionalPortal = (id: number) => {
    setSelectedPortals(selectedPortals.filter((x) => x.Id != id));
  };

  useEffect(() => {
    console.log(settingsData);
    if (settingsData) {
      const resp: ICourtPortalSettings = settingsData.data;

      if (resp.IsActive && !createOnly) {
        setStep(CourtPortalSettingsStep.Settings);
        setShowTabs(true);
        setLoginData({ login: resp.Login, pass: "" });
        setSelectedPortals(resp.Portals.filter((x) => x.IsActive));

        const lastSync = resp.Portals.filter((x) => x.LastSync !== null)
          .map((x) => moment(x.LastSync))
          .sort((a, b) => b.valueOf() - a.valueOf());

        if (lastSync && lastSync.length) {
          setLastSync(lastSync[0]);
        } else {
          setLastSync(null);
        }
      } else {
        setStep(CourtPortalSettingsStep.Login);
        setLoginData({ login: "", pass: "" });
      }

      setPortals(
        resp.Portals.map((x) => {
          return { name: x.Name, value: x.Id.toString() };
        }),
      );
    }
  }, [settingsData, createOnly]);

  useEffect(() => {
    switch (step) {
      case CourtPortalSettingsStep.Login:
        setNextButtonState({
          ...nextButtonState,
          label: t("settings.courtPortal.loginLabel"),
          disabled: true,
        });
        break;
      case CourtPortalSettingsStep.Confirm:
        setNextButtonState({ ...nextButtonState, label: t("common.confirm") });
        break;
      case CourtPortalSettingsStep.Settings:
        setNextButtonState({ ...nextButtonState, label: t("common.finish") });
      case CourtPortalSettingsStep.CaseSettings:
        setNextButtonState({
          ...nextButtonState,
          label: t("settings.courtPortal.syncLabel"),
        });
    }
  }, [step]);

  useEffect(() => {
    if (step !== CourtPortalSettingsStep.Login) return;
    if (!selectedPortals.length) return;
    if (!loginData.login.length || !loginData.pass.length) return;
    setNextButtonState({ ...nextButtonState, disabled: false });
  }, [loginData, selectedPortals]);

  useEffect(() => {
    if (caseSettings?.data) {
      setSyncActiveCases(caseSettings.data.SyncActive);
      setSyncClosedCases(caseSettings.data.SyncClosed);
    }
  }, [caseSettings]);

  if (isLoadingSettings) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.loader}>
          <Spinner></Spinner>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.pathBar}>
        <div className={styles.backButton}>
          {/* <BackIcon onClick={onCanceled}></BackIcon> */}
        </div>
        {/* Integracje <ArrowLeftIcon></ArrowLeftIcon> Portal sądów powszechnych */}
      </div>

      {showTabs && (
        <div className={styles.tabs}>
          {TABS.map((tab) => (
            <div
              className={classNames(
                styles.tab,
                activeTab?.name === tab.name ? styles.active : null,
              )}
              onClick={() => {
                setActiveTab(tab);
                setStep(tab.step);
              }}
            >
              {tab.name}
            </div>
          ))}
        </div>
      )}

      <div className={styles.wrapper2}>
        {step === CourtPortalSettingsStep.Login && (
          <div className={styles.inputs}>
            <div className={styles.hint}>
              {t("settings.courtPortal.loginHint")}{" "}
              <Tooltip title={t("settings.courtPortal.helpVideo")} className={styles.visible}>
                <div>
                  <InfoIcon
                    className={classNames(styles.infoIcon)}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowVideo(!showVideo);
                    }}
                  />
                </div>
              </Tooltip>
            </div>
            <Input
              type="text"
              label="Login"
              onChange={(v) => setLoginData({ ...loginData, login: v })}
              value={loginData.login}
            ></Input>
            <Input
              type="password"
              label={t("settings.courtPortal.authToken")}
              onChange={(v) => setLoginData({ ...loginData, pass: v })}
              value={loginData.pass}
              RightIcon={
                <Tooltip
                  title={t("settings.courtPortal.helpVideo")}
                  className={styles.visible}
                >
                  <div>
                    <InfoIcon
                      className={classNames(styles.infoIcon)}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowVideo(!showVideo);
                      }}
                    />
                  </div>
                </Tooltip>
              }
            ></Input>
            <DropdownMui
              placeholder={t("settings.courtPortal.chooseAppeal")}
              label={t("settings.courtPortal.chooseAppeal")}
              list={portals}
              onChange={onPortalChange}
              allowNull={false}
              clearAfterChoose={true}
            ></DropdownMui>
            <div className={styles.portals}>
              {selectedPortals
                .filter((p) => p.isNew)
                .map((p) => (
                  <Chip
                    element={<div>{p.Name}</div>}
                    onDelete={() => handleRemoveAdditionalPortal(p.Id)}
                  ></Chip>
                  // <div className={styles.portal}>
                  //   <div className={styles.name}>{p.Name}</div>
                  //   <div className={styles.sync}>
                  //     {p.LastSync}
                  //     {!p.LastSync &&
                  //       "Oczekiwanie na pierwszą synchronizację"}
                  //   </div>

                  //   <div className={styles.menu}>
                  //     <RemoveIcon
                  //       title="Usuń"
                  //       onClick={() => handleRemoveAdditionalPortal(p.Id)}
                  //     />
                  //   </div>
                  // </div>
                ))}
            </div>
          </div>
        )}

        {step === CourtPortalSettingsStep.Confirm && (
          <div className={styles.inputs}>
            <div className={styles.hint}>
              {t("settings.courtPortal.confirmDataHint")}
            </div>
            <Input
              label={t("forms.firstName")}
              value={courtPortalUser?.firstName}
              onChange={() => {}}
              disabled={true}
            ></Input>
            <Input
              label={t("forms.lastName")}
              value={courtPortalUser?.lastName}
              onChange={() => {}}
              disabled={true}
            ></Input>

            <Input
              label="PESEL"
              value={courtPortalUser?.pesel}
              onChange={() => {}}
              disabled={true}
            ></Input>
            <Input
              label={t("settings.courtPortal.idDocumentSeries")}
              value={courtPortalUser?.idNumber ?? "(Brak informacji)"}
              onChange={() => {}}
              disabled={true}
            ></Input>
            <Input
              label={t("settings.courtPortal.professionalRepresentative")}
              value={courtPortalUser?.unitNumber ?? "(Brak informacji)"}
              onChange={() => {}}
              disabled={true}
            ></Input>
            <Input
              label={t("settings.courtPortal.licenseNumber")}
              value={courtPortalUser?.cardNumber ?? "(Brak informacji)"}
              onChange={() => {}}
              disabled={true}
            ></Input>

            {courtPortalUser?.birthDate && (
              <Input
                label={t("settings.courtPortal.birthDate")}
                value={moment(courtPortalUser?.birthDate).format("DD.MM.yyyy")}
                onChange={() => {}}
                disabled={true}
              ></Input>
            )}
            <Input
              label={t("settings.courtPortal.birthPlace")}
              value={courtPortalUser?.birthPlace}
              onChange={() => {}}
              disabled={true}
            ></Input>
          </div>
        )}

        {step === CourtPortalSettingsStep.Settings && (
          <div className={classNames(styles.inputs, styles.nomargin)}>
            <>
              <div className={styles.hint}>
                {t("settings.courtPortal.integrationActive")}
              </div>
              <Input
                value={loginData.login}
                label={t("settings.courtPortal.usernameLabel")}
                onChange={() => {}}
                disabled={true}
              ></Input>

              <Input
                value={
                  lastSync
                    ? lastSync.format("DD.MM.yyyy hh:mm:ss")
                    : t("settings.courtPortal.notSyncedYet")
                }
                label={t("settings.lastSync")}
                onChange={() => {}}
                disabled={true}
              ></Input>

              <DropdownMui
                label={t("settings.courtPortal.chooseAppeal")}
                placeholder={t("settings.courtPortal.chooseAppeal")}
                list={portals}
                onChange={onPortalChange}
                allowNull={true}
                clearAfterChoose={true}
              ></DropdownMui>

              <div className={styles.portals}>
                {selectedPortals.map((p) => (
                  <Chip
                    element={<div>{p.Name}</div>}
                    onDelete={() => handleRemoveAdditionalPortal(p.Id)}
                  ></Chip>
                  // <div className={styles.portal}>
                  //   <div className={styles.name}>{p.Name}</div>
                  //   <div className={styles.sync}>
                  //     {p.LastSync}
                  //     {!p.LastSync && "Oczekiwanie na pierwszą synchronizację"}
                  //   </div>

                  //   <div className={styles.menu}>
                  //     <RemoveIcon
                  //       title="Usuń"
                  //       onClick={() => handleRemoveAdditionalPortal(p.Id)}
                  //     />
                  //   </div>
                  // </div>
                ))}
              </div>
            </>
          </div>
        )}
        {step === CourtPortalSettingsStep.CaseSettings && (
          <div className={classNames(styles.inputs, styles.nomargin)}>
            <>
              <div className={styles.tit}>
                {t("settings.courtPortal.casesSyncTitle")}
              </div>
              <div className={styles.hint}>
                {t("settings.courtPortal.casesSyncHint")}
              </div>
            </>
          </div>
        )}
      </div>

      {error && <div className={styles.errorBlock}>{error}</div>}

      {grow && <div className={styles.grow}></div>}

      <div className={styles.buttons}>
        {step === CourtPortalSettingsStep.Settings && (
          <div className={styles.disconnect}>
            {/* <a onClick={() => handleDisconnect(integrationId)}>Odłącz konto</a> */}
          </div>
        )}
        {prevButtonState.show && (
          <HollowButton
            text={prevButtonState.label}
            onClick={onCanceled}
          ></HollowButton>
        )}
        {nextButtonState.show && (
          <Button
            disabled={nextButtonState.disabled || isLoginLoading}
            text={nextButtonState.label}
            onClick={save}
          ></Button>
        )}
      </div>
      {step === CourtPortalSettingsStep.Settings && (
        <div className={classNames(styles.hint, styles.disconnect)}>
          {/* W przypadku odłączenia konta dalsza synchronizacja przestanie być */}
          {/* możliwa. */}
        </div>
      )}

      <Modal
        open={showVideo}
        onClose={() => setShowVideo(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <div className={styles.modal}>
          <div className={styles.header}>
            {t("settings.courtPortal.helpTitle")}
            <CloseIcon
              className={styles.closeIcon}
              onClick={(e) => {
                setShowVideo(false);
                e.stopPropagation();
              }}
            />
          </div>
          <div className={styles.body}>
            <video autoPlay loop muted className={styles.video}>
              <source src={VideoUrl} type="video/mp4" />
              {t("settings.courtPortal.videoNotSupported")}
            </video>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CourtPortalSettings;
